import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

export const HELPING = [
  { v: "son", label: "My son" },
  { v: "daughter", label: "My daughter" },
  { v: "family", label: "A family member" },
  { v: "client", label: "A client" },
];

export function RequestContactDialog({ open, onOpenChange, targetId, targetName, onDone }) {
  const [reason, setReason] = useState("");
  const [helpingWho, setHelpingWho] = useState("");
  const [intro, setIntro] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setHelpingWho(""); setIntro(""); } }, [open]);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please add a reason for your request.");
    if (!helpingWho) return toast.error("Please select who you're helping.");
    setBusy(true);
    try {
      await api.post("/assist/request-contact", { targetId, reason: reason.trim(), helpingWho, intro: intro.trim() || undefined });
      toast.success("Request sent — the member will be asked to approve.");
      onOpenChange(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="request-contact-dialog">
        <DialogHeader><DialogTitle>Request contact — {targetName}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-mint/50 p-3 text-xs text-emerald-900/80" data-testid="rc-free-note">
            Sending a request is free. Viewing contact details requires an active Premium subscription <strong>and</strong> the member's approval.
          </div>
          <div className="space-y-1.5">
            <Label>Reason for requesting contact *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. I'm helping arrange a suitable match and would like to discuss further." data-testid="rc-reason" />
          </div>
          <div className="space-y-1.5">
            <Label>Who are you helping? *</Label>
            <div className="grid grid-cols-2 gap-2">
              {HELPING.map((h) => (
                <button type="button" key={h.v} onClick={() => setHelpingWho(h.v)} data-testid={`rc-helping-${h.v}`}
                  className={`rounded-xl border-2 py-2 text-sm font-medium transition-colors ${helpingWho === h.v ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-900/15 text-emerald-900 hover:bg-mint"}`}>{h.label}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Short introduction (optional)</Label>
            <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="Assalamu alaikum, a little about yourself…" data-testid="rc-intro" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="rc-submit" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Send request</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
